import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { setStation } from "@/lib/cart";
import { Umbrella, Utensils, BedDouble, Star, ArrowLeft } from "lucide-react";

const TYPES = [
  { key: "umbrella", label: "Ombrellone", Icon: Umbrella },
  { key: "table", label: "Tavolo", Icon: Utensils },
  { key: "sunbed", label: "Lettino", Icon: BedDouble },
  { key: "vip", label: "Area VIP", Icon: Star },
];

const SEARCH_PLACEHOLDERS = {
  umbrella: "Cerca numero ombrellone",
  table: "Cerca numero tavolo",
  sunbed: "Cerca numero lettino",
  vip: "Cerca area VIP",
};

// Dedicated station picker used when the user must select a station in the
// middle of another flow (e.g. after clicking the "Chiama Cameriere" bell
// without a station). After selection the user is sent back to the screen
// they came from via location.state.returnTo (falls back to /menu).
export default function SelectStation() {
  const [type, setType] = useState(null);
  const [stations, setStations] = useState([]);
  const [query, setQuery] = useState("");
  const searchRef = useRef(null);
  const loc = useLocation();
  const nav = useNavigate();

  const rawReturn = loc.state?.returnTo;
  // Avoid infinite loop back to this same screen; also fall back if unset.
  const returnTo = rawReturn && rawReturn !== "/select-station" ? rawReturn : "/menu";

  useEffect(() => {
    if (!type) return;
    api.get(`/public/stations?type=${type}`).then((r) => setStations(r.data));
  }, [type]);

  // Auto-focus the search field when a category is selected (opens the mobile keyboard).
  useEffect(() => {
    if (type && searchRef.current) {
      requestAnimationFrame(() => searchRef.current?.focus());
    }
  }, [type]);

  const pick = (s) => {
    setStation(s);
    nav(returnTo, { replace: true });
  };

  const filtered = stations.filter((s) => s.name.toLowerCase().includes(query.toLowerCase()));

  return (
    <div className="min-h-screen px-6 pt-8 pb-24">
      <button
        onClick={() => nav(returnTo, { replace: true })}
        data-testid="select-station-back"
        className="inline-flex items-center gap-1 text-sm text-bronze-600 hover:text-bronze-700"
      >
        <ArrowLeft className="w-4 h-4" /> Torna indietro
      </button>

      <div className="eyebrow mt-4" data-testid="select-station-eyebrow">Selezione postazione</div>
      <h1 className="mt-2 text-4xl sm:text-5xl leading-none" data-testid="select-station-title">
        Da dove stai ordinando?
      </h1>
      <p className="mt-3 text-ink-500 max-w-md">
        Scegli il tipo di postazione, poi il numero. La selezione resterà associata alla tua richiesta.
      </p>

      <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
        {TYPES.map(({ key, label, Icon }) => {
          const active = type === key;
          return (
            <button
              key={key}
              data-testid={`select-type-${key}`}
              onClick={() => { setType(key); setQuery(""); }}
              className={`card-elevated p-4 flex flex-col items-start gap-2 text-left transition-colors ${
                active ? "bg-bronze-600 border-bronze-600 text-white" : "bg-white hover:bg-sand-100"
              }`}
            >
              <Icon className={`w-6 h-6 ${active ? "text-white" : "text-bronze-600"}`} strokeWidth={1.5} />
              <div className="font-serif text-lg leading-none">{label}</div>
            </button>
          );
        })}
      </div>

      {type && (
        <div className="mt-6">
          <input
            key={type}
            ref={searchRef}
            autoFocus
            data-testid="select-station-search"
            placeholder={SEARCH_PLACEHOLDERS[type]}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full rounded-full border border-sand-300 bg-white px-5 py-3 text-base focus:outline-none focus:border-bronze-600"
          />
          <div className="mt-4 grid grid-cols-3 sm:grid-cols-5 md:grid-cols-8 gap-2">
            {filtered.map((s) => (
              <button
                key={s.id}
                data-testid={`select-pick-${s.id}`}
                onClick={() => pick(s)}
                className="card-elevated py-4 text-center hover:border-bronze-600 hover:text-bronze-600 transition-colors font-serif text-xl"
              >
                {s.name}
              </button>
            ))}
            {filtered.length === 0 && <div className="col-span-full text-ink-500 text-sm">Nessuna postazione</div>}
          </div>
        </div>
      )}
    </div>
  );
}
